// Release (DESIGN §3, DATA_CONTRACT L): the engine's release blockers (lifecycle.release_blockers + release_gate.outstanding,
// never release.check, which writes) each with the command that clears it; the release gate; EQR; dating; the assembly
// deadline; the FAC; retention; and the partner's release. Nothing here can be ticked: a blocker clears when the work behind
// it is done, and every date shown is the engine's (no deadline is computed in the browser).
import { useApp } from "../../app/context";
import { Card, EmptyState, ErrorState, StatusPill } from "../../components";
import type { Release } from "../../data/types";
import { payloadFromRun } from "../../lib/commands";
import { fmtDate, plural } from "../../lib/format";
import { useEngagement } from "./Layout";
import { AskOverview, BeforeReleaseItems, DeadlineLine, DeadlineList, DeadlineRecorder, hasOverview } from "./overviewParts";
import { Act, DocCard, Docs, Facts, GapList, NotPublished, SmartDoc, asStrs, commandIn, useAct, useDocs, val, type Open } from "./shared";

/** The command that clears a blocker: the one it names, else the one its sentence names, else an item's resolve. */
function clearing(b: Release["blockers"][number], known: (c: string) => boolean): { command: string; fixed: Record<string, unknown> } | null {
  if (b.command && known(b.command)) return { command: b.command, fixed: {} };
  const item = b.what.match(/^item (\d+)$/);
  if (item) return { command: "items.resolve", fixed: { item: Number(item[1]) } };
  const c = commandIn(b.why, known);
  if (c) return { command: c.command, fixed: c.run ? { __run: c.run } : {} };
  const wp = b.what.match(/^workpaper:(.+)$/);
  if (wp && /sign-?off/.test(b.why)) return { command: "signoff.record", fixed: { workpaper: wp[1] } };
  if (wp && /not rendered/.test(b.why)) return { command: "workpaper.render", fixed: { workpaper: wp[1] } };
  return null;
}

export function ReleasePage() {
  const { model, id } = useEngagement();
  const { catalogue } = useApp();
  const { open, drawer } = useAct();
  const docs = useDocs("completion");
  const r = model.release;
  const h = model.header;
  if (model.failed.release) return <ErrorState error={`The engine could not compute release readiness: ${model.failed.release}`} />;
  if (!r) return <EmptyState title="Release readiness is not published yet." />;
  const known = (c: string) => catalogue.has(c);
  const assembly = h.deadlines.find((d) => d.what === "assembly");
  const fac = h.deadlines.find((d) => d.what === "fac");
  return (
    <div className="space-y-4">
      <Card title={r.released ? "Released" : r.ready ? "Ready for the partner's release" : `${plural(r.blockers.length, "blocker")} before release`}
        action={<span className="text-xs text-muted">as of {fmtDate(r.asOf)} (engine)</span>}>
        <p className="mb-3 text-sm text-muted">Computed by the engine from the record. Nothing here can be ticked; each blocker clears when the work behind it is done.</p>
        {r.ready ? <StatusPill state="ready" label="No blockers" /> : (
          <ul className="divide-y divide-line" aria-label="Release blockers">
            {r.blockers.map((b, i) => {
              const c = clearing(b, known);
              const fixed = c?.fixed.__run ? payloadFromRun(String(c.fixed.__run), catalogue.get(c.command)) : c?.fixed;
              return (
                <li key={i} className="flex flex-wrap items-start gap-2 py-2 text-sm">
                  <StatusPill state="blocked" label={b.group ?? b.what} />
                  <span className="min-w-0 flex-1">{b.why}</span>
                  {c ? <Act command={c.command} title={b.why} open={open} fixed={fixed} label={`${c.command}…`} /> : <span className="text-xs text-muted">no single command clears this</span>}
                </li>
              );
            })}
          </ul>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          <Act command="release.record" title="Release the report (partner's own act)" open={open} label="Release the report…" kind="primary" size="md"
            reason={r.released ? "Already released." : !r.ready ? `The engine lists ${plural(r.blockers.length, "blocker")}; it refuses a release until they clear.` : null} />
          <span className="text-xs text-muted">The report date is a fact you type; the engine stamps the act.</span>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Deadlines" className="min-w-0" action={h.overviewAsOf ? <span className="text-xs text-muted">as of {fmtDate(h.overviewAsOf)} (engine)</span> : undefined}>
          {h.overviewError ? <p role="alert" className="text-sm text-blocked">The engine could not compute the overview: {h.overviewError}</p> : hasOverview(h) ? (
            <div className="space-y-3">
              {h.nextDeadline && <p className="text-sm">Next: <DeadlineLine d={h.nextDeadline} /></p>}
              <DeadlineList deadlines={h.deadlines} />
            </div>
          ) : <AskOverview />}
          <div className="mt-3 border-t border-line pt-3"><DeadlineRecorder engagementId={id} current={h.deadlines.find((d) => d.what === "report_due")} /></div>
        </Card>
        <Card title={`Before-release items · ${h.counts.beforeRelease}`} className="min-w-0">
          <BeforeReleaseItems h={h} />
          <p className="mt-2 text-xs text-muted">Resolved, or overridden by the partner with a written reason, before release (ADR-0002). A sign-off made despite a caution adds one here.</p>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Release gate" className="min-w-0">{r.gate.length ? <ul className="space-y-1 text-sm">{r.gate.map((g) => <li key={g.key}><StatusPill state="blocked" label={g.key} /> {g.text}{g.reasons.length > 0 && <span className="block text-xs text-muted">{g.reasons.join("; ")}</span>}</li>)}</ul> : <p className="text-sm">Nothing outstanding.</p>}</Card>
        <Docs state={docs}>
          {(d) => (
            <>
              <DocCard docs={d} k="eqr" title="Engagement quality review" action={<span className="flex flex-wrap gap-1"><Act command="eqr.determine" title="Determine whether an EQR is required (partner)" open={open} label="Determine…" /><Act command="eqr.complete" title="Complete the EQR (the reviewer's own act)" open={open} label="Complete…" /></span>}>
                {(doc) => <EqrView doc={doc} eqrBlockers={r.eqrBlockers} open={open} />}
              </DocCard>
              <DocCard docs={d} k="dating" title="Dating" action={<Act command="dating.record" title="Record a dating fact" open={open} label="Record…" />}>
                {(doc) => <div className="space-y-2"><Facts items={[["Report date checked", val(doc.report_date)], ["Engine check", doc.ok ? <StatusPill key="ok" state="passed" label="OK" /> : <StatusPill key="no" state="blocked" label="Not yet" />]]} />
                  <GapList gaps={[...asStrs(doc.problems), ...r.datingMissing.filter((m) => !asStrs(doc.problems).includes(m))]} open={open} title="Missing" /></div>}
              </DocCard>
            </>
          )}
        </Docs>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Assembly" className="min-w-0" action={<Act command="assembly.lock" title="Lock the assembled file (manager's own act)" open={open} label="Lock…" reason={h.assembledAt ? "Already assembled." : !h.releasedAt ? "The file is assembled after the report is released." : null} />}>
          <Facts items={[["Released", h.releasedAt ? fmtDate(h.releasedAt) : "not yet"], ["Assembled", h.assembledAt ? fmtDate(h.assembledAt) : "not yet"],
            ["Assembly due", assembly ? <DeadlineLine key="a" d={assembly} /> : null]]} />
          {assembly?.missing && <p className="mt-2 text-xs text-muted">{assembly.missing}</p>}
          {!assembly && <p className="mt-2 text-xs text-muted">The engine has not published the assembly deadline with this engagement.</p>}
          <div className="mt-2"><Act command="postassembly.add" title="Add documentation after assembly (with the reason)" open={open} label="Post-assembly addition…" /></div>
        </Card>
        <Docs state={docs}>
          {(d) => (
            <>
              {h.singleAudit ? (
                <DocCard docs={d} k="fac" title="FAC (single audit)" action={<Act command="fac.record" title="Record a FAC event" open={open} label="Record…" />}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
              ) : (
                <Card title="FAC" className="min-w-0"><p className="text-sm text-muted">No single audit: nothing is due at the Federal Audit Clearinghouse.</p>{fac && <p className="text-sm"><DeadlineLine d={fac} /></p>}</Card>
              )}
              <DocCard docs={d} k="retention" title="Retention" action={<span className="flex flex-wrap gap-1"><Act command="retention.extend" title="Extend retention (partner)" open={open} label="Extend…" /><Act command="retention.hold" title="Place or lift a legal hold (partner)" open={open} label="Hold…" /></span>}>
                {(doc) => <SmartDoc doc={doc} open={open} />}
              </DocCard>
            </>
          )}
        </Docs>
      </div>
      <Docs state={docs}>
        {(d) => d.some((x) => x.kind === "post_issuance") ? (
          <DocCard docs={d} k="post_issuance" title="After release: subsequent facts and omitted procedures" action={<Act command="postissue.record" title="Record a matter found after release" open={open} label="Record…" />}>{(doc) => <SmartDoc doc={doc} open={open} />}</DocCard>
        ) : <NotPublished what="post-issuance matters" command="postissue.list" />}
      </Docs>
      <p className="text-xs text-muted">Backups run on the engine's machine (<span className="font-mono">yvc backup create</span>). Engagement {id}.</p>
      {drawer}
    </div>
  );
}

function EqrView({ doc, eqrBlockers, open }: { doc: Record<string, unknown>; eqrBlockers: string[]; open: Open }) {
  return (
    <div className="space-y-2">
      <Facts items={[["Required", doc.required === true ? "yes" : doc.required === false ? "no" : val(doc.required)], ["Reasons", asStrs(doc.reasons).join("; ") || null],
        ["Reviewer", val(doc.reviewer)], ["Completed", doc.completed_at ? fmtDate(String(doc.completed_at)) : "not yet"], ["Policy", val(doc.policy_status)]]} />
      <GapList gaps={[...asStrs(doc.blockers), ...eqrBlockers.filter((x) => !asStrs(doc.blockers).includes(x))]} open={open} title="EQR blockers" />
    </div>
  );
}
